import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { appMessage as message } from '../utils/appMessage';

/**
 * "마운트 시 조회 → loading 관리 → 실패 시 토스트"라는 공통 뼈대. `useEntityList`가
 * 감싸는 리스트 전용 로직(merge/replace) 없이, 응답을 그대로 하나의 값으로 받아
 * 두는 화면(예: 여러 매장을 한 번에 집계한 단일 객체를 받는 SuperOverviewPage)에 쓴다 —
 * 응답이 `T[]`이고 각 원소가 `id`를 가진 "엔티티 목록"이면 `useEntityList`를 쓴다.
 *
 * 목록 조회 경쟁 방지: 마지막에 시작한 조회의 응답만 반영한다(늦게 도착한 옛 응답 무시) —
 * 새로고침 버튼 연타나 필터 전환 중 재조회가 겹치면 먼저 보낸 느린 응답이 나중에 도착해
 * 최신 상태를 덮어쓸 수 있다.
 *
 * 실패 알림 중복 방지(2026-10-20 리뷰): 화면(AppLoadError 등)이 이미 오류를 보여 주는데
 * 훅이 토스트까지 띄우면 같은 오류가 두 번 보인다. 그래서 **아직 보여 줄 데이터가 없는
 * 첫 조회 실패는 화면에만 맡기고 토스트를 띄우지 않는다**. 이전 조회 결과가 화면에 남아
 * 있는 재조회 실패는 토스트로 알린다(토스트 문구는 여기 한 곳에서만 쓴다).
 *
 * 인라인 배너 화면(2026-10-21 PR #39 사후 리뷰 → 같은 날 회귀 수정): 재조회 실패를
 * `AppLoadError inline` 줄로 알리는 화면은 **마운트 동안 `setInlineError(true)`** 로 토스트를
 * 끈다(`useInlineLoadError` 헬퍼). 처음엔 이 값을 `useCards` 에 전역으로 고정했는데, 카드
 * 목록은 여러 화면이 공유하는 전역 훅이라 배너를 그리지 않는 화면(상품 등록 후 재조회·
 * 반영 로그·매입 등)의 재조회 실패까지 토스트도 배너도 없이 묻혔다. 그래서 정적 옵션이
 * 아니라 **화면 단위 런타임 토글**로 바꾼다 — 켠 화면에서만 토스트가 꺼지고, 벗어나면
 * (언마운트) 다시 켜져 다른 화면은 예전처럼 토스트로 알린다.
 *
 * `hasLoadedOnce` 는 "성공한 조회가 한 번이라도 있었는지"다. 화면은 `data.length === 0` 으로
 * 첫 조회 실패와 진짜 0건을 구분하려 했는데, 0건이 정상 응답인 화면에서는 재조회 실패가
 * 전체 오류 화면으로 둔갑했다 — 이 값으로 "보여 줄 이전 결과가 있는지"를 판단한다.
 *
 * @param fetchFn 데이터를 가져오는 함수 — 매 렌더마다 새로 만들지 않는 안정적인 참조여야
 *                한다(그렇지 않으면 reload가 매번 바뀌어 재조회 루프가 생긴다).
 */
export function useAsyncData<T>(
  fetchFn: () => Promise<T>,
  errorMessage: string,
  initial: T,
) {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  // 서버에서 새로 받아 넣은 횟수 — 화면 안 수정(setData)과 구분해 "목록을 다시 받았다" 를 따라갈 때 쓴다
  // (감사 card-controltower#121 — PriceHub 변경 목록이 카드 목록 재조회를 따라가게).
  const [loadVersion, setLoadVersion] = useState(0);
  const reqSeq = useRef(0);

  // 인라인 배너 화면이 켜는 토스트 억제 스위치. reload 가 이 값에 의존하면 화면이 켜고 끌
  // 때마다 reload 참조가 바뀌어 재조회가 한 번 더 나간다 — ref 로만 따라가 reload 는 고정.
  const inlineErrorRef = useRef(false);
  const setInlineError = useCallback((on: boolean) => {
    inlineErrorRef.current = on;
  }, []);

  // reload 는 fetchFn 에만 의존하므로 catch 안에서 data 를 직접 읽으면 첫 렌더의
  // 낡은 값을 본다 — 최신 data 를 ref 로 따라가며 "화면에 보여 줄 데이터가 있는지" 판단한다.
  const dataRef = useRef<T>(data);
  dataRef.current = data;

  // 소비자가 새 데이터(잡 완료 병합·가격 수정 반영 등)를 넣으면 오류 표시를 내린다 —
  // 조회 실패 안내가 실제 화면 상태와 어긋난 채 남지 않게(2026-10-20 리뷰).
  const setDataSafe = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    setError(null);
    setData(next);
  }, []);

  // 돌려주는 값: 이 조회가 성공해 데이터를 넣었는가(늦어서 버려진 응답 · 실패면 false).
  const reload = useCallback(async (): Promise<boolean> => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      if (seq === reqSeq.current) {
        setData(result);
        setHasLoadedOnce(true);
        setLoadVersion((v) => v + 1);
        return true;
      }
      return false;
    } catch (err) {
      if (seq === reqSeq.current) {
        const current = dataRef.current;
        const hasData = Array.isArray(current) ? current.length > 0 : current != null;
        if (hasData && !inlineErrorRef.current) message.error(errorMessage);
        setError(err instanceof Error ? err : new Error(String(err)));
      }
      return false;
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFn]);

  useEffect(() => { reload(); }, [reload]);

  const retry = useCallback(() => reload(), [reload]);

  return { data, setData: setDataSafe, loading, reload, error, retry, hasLoadedOnce, setInlineError, loadVersion };
}

/**
 * 화면이 인라인 오류 배너를 그리는 동안만 토스트를 끈다 — 마운트 시 켜고, 언마운트 때
 * 되돌린다(2026-10-21 회귀 수정). 배너를 그리지 않는 화면은 이 헬퍼를 쓰지 않으므로
 * 재조회 실패가 예전처럼 토스트로 보인다.
 */
export function useInlineLoadError(setInlineError: (on: boolean) => void) {
  useEffect(() => {
    setInlineError(true);
    return () => setInlineError(false);
  }, [setInlineError]);
}
